import React, { useState, Fragment } from "react";
import type { ServiceHistoryEventType } from "../../../../../../shared-logic/types/UscCommonTypes";
import { formatNumberIntl } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { UscLabel, useUscLabel } from "../../../../../utils/labels";
import { useUscSelector } from "../../../../../redux/store";
import { DfServiceHistoryCode } from "../../../../../../shared-logic/utils/dfConstants";
import IconArrowDown from "../../../../../../../common-deprecated/svg/lexus/arrow-down.svg";
import DropdownContainer from "../../../../../../../common-deprecated/components/dropdown/DropdownContainer";
import Disclosure from "../../../styles/molecules/Disclosure";
import Icon from "../../../styles/Icon";
import IconManual from "../../../assets/visuals/manual.svg";
import Text from "../../../styles/atoms/Text";
import { useBreakpoint } from "../../../../../../../common-deprecated/themes/common";
import * as Styles from "./ServiceHistory.styles";
import * as CarDetailsStyles from "../CarDetails.styles";
import { getFormattedServiceEventDate } from "../../../../../utils/pdpUtils";

type YearType = { year: number; events: ServiceHistoryEventType[] };

type ServiceEventPropsType = {
    event: ServiceHistoryEventType;
    cultureName: string;
    first?: boolean;
};

export const ServiceEvent = ({ event, cultureName, first }: ServiceEventPropsType): JSX.Element => (
    <Styles.Event showBorder={first} role="listitem">
        <Styles.EventHeader>
            <Styles.EventTitle>{getFormattedServiceEventDate(event.date, cultureName)}</Styles.EventTitle>
            {event.mileage.value > 0 && (
                <Styles.EventMileagePoint>
                    {formatNumberIntl(event.mileage.value, cultureName)} {event.mileage.unit.description}
                </Styles.EventMileagePoint>
            )}
        </Styles.EventHeader>
        <Styles.EventDescription>{event.description || event.type.description}</Styles.EventDescription>
        {event.officialDealer && (
            <Styles.EventOfficialDealer>
                <UscLabel label="uscPdpServiceOfficialDealer" />
            </Styles.EventOfficialDealer>
        )}
    </Styles.Event>
);

const ServiceHistory = (): JSX.Element | null => {
    const vehicleResult = useUscSelector((state) => state.vehicle.result);
    const enableServiceHistory = useUscSelector((state) => state.uscSettings.enableServiceHistory);
    const visibleHistoryEventTypes = useUscSelector((state) => state.uscSettings.visibleVehicleHistoryEvents);
    const cultureName = useUscSelector((state) => state.commonSettings.culture.name);
    const title = useUscLabel("uscPdpServiceTitle");
    const [showAllEvents, setShowAllEvents] = useState<boolean>(false);

    const isMobile = useBreakpoint("down", "md");

    if (!vehicleResult || !enableServiceHistory || !vehicleResult?.history?.events?.length) return null;

    const { mileage, history } = vehicleResult;
    const { registrationDate, events } = history;

    const filteredEvents = events.filter((event) => visibleHistoryEventTypes.includes(event.type.code));

    if (!filteredEvents.length) return null;

    const yearsOfService = registrationDate
        ? Math.round((Date.now() - new Date(registrationDate).getTime()) / 31622400 / 1000) // 31622400 is 1 year
        : 0;
    const checkCount = filteredEvents.filter((event) => event.type.code === DfServiceHistoryCode.Maintenance).length; // TODO: remove this? GFI counts all events, even delivery

    // group events based on year
    const years = filteredEvents.reduce<YearType[]>((yearList, event) => {
        const eventYear = new Date(event.date).getFullYear();
        const foundYear = yearList.find((item) => item.year === eventYear);
        if (foundYear) foundYear.events.push(event);
        else yearList.push({ year: eventYear, events: [event] });
        return yearList;
    }, []);

    return (
        <Disclosure variant="specs" ariaLabel={title}>
            <summary>
                <div>
                    {!isMobile && <IconManual />}
                    <Text tag="h3" variant="h6">
                        {title}
                    </Text>
                </div>
                <CarDetailsStyles.IconWrapper>
                    <Icon variant="chevron-down" />
                </CarDetailsStyles.IconWrapper>
            </summary>
            <Styles.Description>
                <UscLabel label="uscPdpServiceDescription" />
            </Styles.Description>
            <Styles.Card>
                <Styles.SpecList>
                    {yearsOfService > 0 && (
                        <Styles.SpecItem>
                            <Icon variant="car" />
                            <UscLabel
                                label={
                                    yearsOfService === 1 ? "uscPdpServiceYearsCountSingle" : "uscPdpServiceYearsCount"
                                }
                                params={{ count: yearsOfService }}
                            />
                        </Styles.SpecItem>
                    )}
                    {!!mileage && (
                        <Styles.SpecItem>
                            <Icon variant="steering-wheel" /> {formatNumberIntl(mileage.value, cultureName)}{" "}
                            {mileage.unit.description}
                        </Styles.SpecItem>
                    )}
                    {checkCount > 0 && (
                        <Styles.SpecItem>
                            <Icon variant="steering-wheel" />
                            <UscLabel
                                label={checkCount === 1 ? "uscPdpServiceChecksCountSingle" : "uscPdpServiceChecksCount"}
                                params={{ count: checkCount }}
                            />
                        </Styles.SpecItem>
                    )}
                </Styles.SpecList>

                {/* 
                    The first event (if it exists) is always shown, the rest are hidden in a dropdown below.
                    If there is only one event, the dropdown is not shown.
                */}
                <Text tag="h4" variant="h6">
                    <UscLabel label="uscPdpServiceHistoryTitle" />
                </Text>

                <div role="list">
                    <ServiceEvent event={filteredEvents[0]} cultureName={cultureName} first />

                    <DropdownContainer show={showAllEvents}>
                        {years.map((year, yearIndex) => (
                            /**
                             * We loop through the years, and for each year we loop through the events.
                             * The first event (index 0) is skipped in this loop, because it is always shown
                             * outside the dropdown (see above)
                             */
                            <Fragment key={year.year}>
                                {yearIndex > 0 && (
                                    <Styles.YearSeparator role="time">
                                        <span>{year.year}</span>
                                    </Styles.YearSeparator>
                                )}
                                {year.events
                                    .filter((_, eventIndex) => !(yearIndex === 0 && eventIndex === 0))
                                    .map((event) => (
                                        <ServiceEvent
                                            key={event.date + event.description}
                                            event={event}
                                            cultureName={cultureName}
                                        />
                                    ))}
                            </Fragment>
                        ))}
                    </DropdownContainer>
                </div>

                {filteredEvents.length > 1 && (
                    <Styles.ShowMore onClick={() => setShowAllEvents(!showAllEvents)} rotateIcon={showAllEvents}>
                        <UscLabel label={showAllEvents ? "uscShowLess" : "uscShowMore"} />
                        <IconArrowDown />
                    </Styles.ShowMore>
                )}
            </Styles.Card>
        </Disclosure>
    );
};

export default ServiceHistory;
